import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations, TranslationKeys, isSupportedLanguage, isRtlLanguage } from "@/i18n/translations";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type NestedKeyOf<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${NestedKeyOf<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

export type TranslationKey = NestedKeyOf<TranslationKeys>;

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => Promise<void>;
  t: (key: string) => string;
  formatNumber: (num: number | string) => string;
  isLoading: boolean;
  isRtl: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const DEFAULT_LANGUAGE = "english";
const LANGUAGE_STORAGE_KEY = "app-language";

function getNestedValue(obj: any, path: string): string {
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    if (result && typeof result === "object" && key in result) {
      result = result[key];
    } else {
      return path; // Return the key if not found
    }
  }
  return typeof result === "string" ? result : path;
}

function updateDocumentDirection(lang: string) {
  // Keep document always LTR - RTL is applied only to overlay components (dialogs, popovers, etc.)
  document.documentElement.dir = "ltr";
  document.documentElement.lang = lang === "arabic" ? "ar" : lang === "english" ? "en" : lang.slice(0, 2);
}

// Arabic-Indic numerals mapping
const arabicNumerals: Record<string, string> = {
  "0": "٠",
  "1": "١",
  "2": "٢",
  "3": "٣",
  "4": "٤",
  "5": "٥",
  "6": "٦",
  "7": "٧",
  "8": "٨",
  "9": "٩",
};

function toArabicNumerals(value: string): string {
  return value.replace(/[0-9]/g, (digit) => arabicNumerals[digit] || digit);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<string>(() => {
    // Initialize from localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
      updateDocumentDirection(stored);
      return stored;
    }
    return DEFAULT_LANGUAGE;
  });
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const isRtl = isRtlLanguage(language);

  // Load language from profile on mount
  useEffect(() => {
    const loadLanguage = async () => {
      if (user) {
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("language")
            .eq("id", user.id)
            .single();

          if (profile?.language) {
            setLanguageState(profile.language);
            localStorage.setItem(LANGUAGE_STORAGE_KEY, profile.language);
            updateDocumentDirection(profile.language);
          }
        } catch (error) {
          console.error("Error loading language preference:", error);
        }
      }
      setIsLoading(false);
    };

    loadLanguage();
  }, [user]);

  const setLanguage = useCallback(
    async (lang: string) => {
      setLanguageState(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      updateDocumentDirection(lang);

      // Persist to profile if user is logged in
      if (user) {
        try {
          await supabase
            .from("profiles")
            .update({ language: lang })
            .eq("id", user.id);
        } catch (error) {
          console.error("Error saving language preference:", error);
        }
      }
    },
    [user]
  );

  const t = useCallback(
    (key: string): string => {
      // Use the current language's translations, fallback to English
      const currentTranslations = isSupportedLanguage(language)
        ? translations[language]
        : translations.english;

      const value = getNestedValue(currentTranslations, key);
      
      // Fallback to English if key not found in current language
      if (value === key && language !== "english") {
        return getNestedValue(translations.english, key);
      }
      
      return value;
    },
    [language]
  );

  const formatNumber = useCallback(
    (num: number | string): string => {
      const strValue = String(num);
      if (language === "arabic") {
        return toArabicNumerals(strValue);
      }
      return strValue;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, formatNumber, isLoading, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Convenience hook for just translations
export function useTranslation() {
  const { t, language, isRtl, formatNumber } = useLanguage();
  return { t, language, isRtl, formatNumber };
}
